<template>
  <div class="profile-tab">
    <MemberLevelProgressBlock v-if="memberId && useFeatures.viewMemberLevel" ref="progressBlock" :member-id="memberId" />
    <PageTitle title="" btnFeature="admin.member.update" @btnClick="showEditDialog = true" />

    <div class="space-y-[24px]">
      <!-- 會員進度條區塊 -->
      <!-- LINE 資訊區塊 -->
      <div class="section-block">
        <BaseElForm
          label-position="left"
          label-width="180px"
          class="form"
        >
          <BaseElFormItem :label="$t('member.tab.memberInfo.lineBinding')" prop="memberAuthLine">
            <div class="inline-flex align-middle gap-[4px]">
              <div class="flex items-start gap-2">
                <SVGIcon
                  v-if="lineAuthData.isMemberAuthLine"
                  icon="common-check"
                  fill="var(--primary-100)"
                  width="24"
                  height="24"
                />
                <SVGIcon
                  v-else
                  icon="common-close"
                  fill="var(--danger)"
                  width="24"
                  height="24"
                />
                <!-- last member auth line log -->
                <div class="leading-normal">
                  <div class="flex gap-[8px] items-center">
                    <p v-if="lineAuthData.isMemberAuthLine">{{ $t('member.tab.memberInfo.completedLineBinding') }}</p>
                    <p v-else>{{ lineAuthData.lastMemberAuthLineLog?.unBindReasean || $t('member.tab.memberInfo.lineBindingIncomplete') }}</p>
                    <BaseElButton
                      v-if="useFeatures.updateMember && useFeatures.refreshMemberLineProfile"
                      type="text"
                      class="underline text-primary-100 !text-[14px] p-0"
                      :loading="refreshLoading"
                      @click="onRefreshLineProfile"
                    >
                      {{ $t('member.tab.memberInfo.checkBindingAndName') }}
                    </BaseElButton>
                  </div>
                  <p v-if="lineAuthData.isMemberAuthLine" class="text-xs text-gray-60">{{ $t('member.tab.memberInfo.lineBindingNote') }}</p>
                  <template v-else>
                    <p v-if="lineAuthData.lastMemberAuthLineLog?.createdAt" class="text-xs text-gray-60">
                      {{ $t('member.tab.memberInfo.unbindTime') }}{{ formatDate(lineAuthData.lastMemberAuthLineLog.createdAt, 'YYYY/MM/DD HH:mm') }}
                    </p>
                  </template>
                </div>
              </div>
            </div>
            <div v-if="lineAuthData.isMemberAuthLine">
              <BaseElButton
                v-if="useFeatures.updateMember && useFeatures.deleteMemberAuthLine"
                type="text"
                class="underline !text-danger !text-[14px] p-0"
                :loading="refreshLoading"
                @click="showDeBindDialog = true"
              >
                {{ $t('member.tab.memberInfo.unbind') }}
              </BaseElButton>
            </div>
          </BaseElFormItem>

          <BaseElFormItem :label="$t('member.tab.memberInfo.lineFriendName')">
            <span>{{ lineAuthData.memberAuthLine?.displayName || '-' }}</span>
          </BaseElFormItem>
        </BaseElForm>
      </div>
      <!-- 會員資訊區塊 -->
      <div class="section-block">
        <BaseElForm
          ref="form"
          :model="formData"
          label-position="left"
          label-width="180px"
          class="form"
        >
          <BaseElFormItem :label="$t('member.tab.memberInfo.userStatus')">
            <span>{{ memberStatusLabel(formData.memberStatus, formData.isBlock) }}</span>
          </BaseElFormItem>

          <BaseElFormItem
            v-if="usePospal"
            v-featureKey="'admin.shopCustomModule.posPalMemberWallet'"
            :label="$t('member.tab.memberInfo.pospalBinding')"
          >
            {{ get(displayMember, 'UserInfo.systemCustom.pospalMemberNumber', '-') }}
          </BaseElFormItem>

          <BaseElFormItem v-if="useMemberOpentix" :label="$t('member.tab.memberInfo.opentixBinding')">
            <div class="icon__vertical-align">
              <SVGIcon
                v-if="opentixBinded"
                icon="common-check"
                fill="var(--primary-100)"
                width="24"
                height="24"
              />
              <SVGIcon
                v-else
                icon="common-close"
                fill="var(--danger)"
                width="24"
                height="24"
              />
            </div>
          </BaseElFormItem>

          <!-- 新增：凱惠 POS 綁定 -->
          <BaseElFormItem v-if="useTKHSPC" v-featureKey="'planModule.tkhspc.enable'" :label="$t('member.tab.memberInfo.tkhspcBinding')">
            {{ displayMember?.UserInfo?.systemCustom?.tkhspcUid ? displayMember?.UserInfo.systemCustom.tkhspcUid : '-' }}
          </BaseElFormItem>

          <!-- 新增：CYJ 凱惠 POS 綁定 -->
          <BaseElFormItem v-if="useCyjPos" v-featureKey="'planModule.cyjPos.enable'" :label="$t('member.tab.memberInfo.cyjPosBinding')">
            {{ displayMember?.UserInfo?.systemCustom?.cyjPosCsid ? displayMember?.UserInfo.systemCustom.cyjPosCsid : '-' }}
          </BaseElFormItem>

          <BaseElFormItem :label="$t('member.tab.memberInfo.loginMethod')" prop="memberUserAuth">
            <div class="icon__vertical-align">
              <span
                v-if="
                  !displayMember.User?.AuthLine &&
                    !displayMember.User?.AuthFB &&
                    !displayMember.User?.AuthPhoneAccount
                "
              >{{ $t('member.tab.memberInfo.notLoggedIn') }}</span>
              <img
                v-if="displayMember.User?.AuthLine"
                src="@/assets/member/icon-auth-line.svg"
                alt="Line auth icon"
              >
              <img
                v-if="displayMember.User?.AuthFB"
                src="@/assets/member/icon-auth-facebook.svg"
                alt="Facebook auth icon"
              >
              <img
                v-if="displayMember.User?.AuthPhoneAccount"
                src="@/assets/member/icon-auth-phone.svg"
                alt="Phone Account auth icon"
              >
            </div>
          </BaseElFormItem>

          <BaseElFormItem v-if="useFeatures.viewMemberLevel" :label="$t('member.tab.memberInfo.memberLevel')" prop="level">
            <div class="flex items-center" style="gap: 12px">
              <p v-if="formData.level > 0">
                {{ getDisplayLevel() }}
              </p>
              <p v-else>{{ $t('member.tab.memberInfo.noLevel') }}</p>
              <BaseElButton
                v-if="useFeatures.viewMemberLevel"
                type="text"
                class="underline text-primary-100"
                @click="showMemberLevelDetail = true"
              >
                <span style="font-size: 12px">{{ $t('member.tab.memberInfo.levelChangeHistory') }}</span>
              </BaseElButton>
              <BaseElButton
                v-if="useFeatures.viewMemberLevel"
                type="text"
                class="underline text-primary-100"
                @click="showProgressDrawer = true"
              >
                <span style="font-size: 12px">{{ $t('member.tab.memberInfo.levelProgressBar') }}</span>
              </BaseElButton>
            </div>
          </BaseElFormItem>

          <BaseElFormItem v-if="memberPolicyData.configData.enable" :label="$t('member.tab.memberInfo.benefitPolicy')">
            {{ displayData.memberPolicyStatus }}
          </BaseElFormItem>

          <BaseElFormItem :label="$t('member.tab.memberInfo.phoneNumber')" prop="phone">
            <div>
              {{ formData.phone || $t('member.tab.memberInfo.notBound') }}
            </div>
          </BaseElFormItem>

          <BaseElFormItem
            v-for="field in baseUserSetting"
            :key="field.key"
            :label="field.name"
            :prop="field.key"
          >
            <span>
              {{ getBaseFormData(field) || $t('member.tab.memberInfo.notFilled') }}
            </span>
          </BaseElFormItem>
          <BaseElFormItem
            v-for="field in customUserSetting"
            v-show="
              field.type !== 'companyDirectoryFirmTaxId' ||
                (showCompany && field.type === 'companyDirectoryFirmTaxId')
            "
            :key="field.key"
            :label="field.name"
            :prop="field.key"
          >
            <span v-if="!['restrictedImage'].includes(field.type)" style="white-space: break-spaces;">{{ getCustomFormData(field) || $t('member.tab.memberInfo.notFilled') }}</span>
            <div v-if="field.type === 'restrictedImage'" class="flex items-center" style="gap: 12px">
              <p>{{ getCustomFormData(field) ? $t('member.tab.memberInfo.uploaded') : $t('member.tab.memberInfo.notUploaded') }}</p>
              <BaseElButton v-if="getCustomFormData(field) && useFeatures.viewRestrictedFile" type="text" class="text-primary-100 underline" @click="onViewFile(getCustomFormData(field))">
                <span style="font-size: 12px">{{ $t('member.tab.memberInfo.view') }}</span>
              </BaseElButton>
            </div>
          </BaseElFormItem>

          <BaseElFormItem :label="$t('member.tab.memberInfo.createOrigin')">
            <span>{{ fetchedMember ? memberOrigin(fetchedMember.origin, fetchedMember.originText) : '' }}</span>
          </BaseElFormItem>
          <BaseElFormItem label="來源分店">
            <span>{{ sourceShopName }}</span>
          </BaseElFormItem>
          <BaseElFormItem :label="$t('member.tab.memberInfo.createdAt')">
            <span>{{ fetchedMember ? formatDate(fetchedMember.createdAt, 'YYYY/MM/DD HH:mm:ss') : '' }}</span>
          </BaseElFormItem>
        </BaseElForm>
      </div>
    </div>

    <!-- 解綁對話框 -->
    <DeBindAuthLineDialog
      :show.sync="showDeBindDialog"
      :member="member"
      @debind="onDeBindSuccess"
    />
    <MemberProfileEdit
      v-if="showEditDialog"
      :member="formData"
      :levelList="levelList"
      :baseUserSetting="baseUserSetting"
      :customUserSetting="customUserSetting"
      :opentixBinded="opentixBinded"
      :useMemberOpentix="useMemberOpentix"
      @refresh="$emit('update'); refresh()"
      @close="showEditDialog = false"
    />

    <MemberLevelDetail
      v-if="showMemberLevelDetail"
      :memberId="memberId"
      @close="showMemberLevelDetail = false"
    />
    <MemberLevelProgressDrawer
      v-if="showProgressDrawer && memberId"
      :member-id="memberId"
      @close="showProgressDrawer = false"
    />
  </div>
</template>

<script>
import MemberProfileEdit from './components/MemberProfileEdit.vue'
import MemberLevelDetail from '@/views/Members/components/MemberLevelDetail/MemberLevelDetail.vue'
import MemberLevelProgressDrawer from '@/views/Members/components/MemberLevelProgressDrawer/MemberLevelProgressDrawer.vue'
import DeBindAuthLineDialog from './components/DeBindAuthLineDialog.vue'
import MemberLevelProgressBlock from './components/MemberLevelProgressBlock.vue'
import { mapGetters } from 'vuex'
import { GetMemberAuthLine, RefreshMemberLineProfile, FindMember } from '@/api/member'
import { SimpleFindCompanyContactMenu } from '@/api/company/contactMenu'
import { genderName } from '@/config/userInfoOptions'
import { extractList, isEmptyObj } from '@/utils/helper'
import { formatDate } from '@/utils/date'
import { handleMemberOrigins } from '@/utils/member'
import { find, get } from 'lodash'
import { defineComponent, computed, ref, reactive, onMounted } from 'vue'
import { useCompany } from '@/use/company'
import { GetUserInfoSetting } from '@/api/org/org'
import { CheckMemberOpentixBinding } from '@/api/opentix'
import { useShop } from '@/use/shop'
import { useFetch } from '@/use/fetch'
import { usePermissions } from '@/use/permissions'
import { GetCustomModule } from '@/api/customModule'
import { GetMemberPolicyConfig } from '@/api/memberPolicy'
import store from '@/store'
import { GetRestrictedFileUrl } from '@/api/file'
import { api as viewerApi } from 'v-viewer'
import { useMemberContext } from '@/use/useMemberContext'
import { t } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'MemberInfoTab',
  components: { MemberProfileEdit, MemberLevelDetail, DeBindAuthLineDialog, MemberLevelProgressBlock, MemberLevelProgressDrawer },
  props: {
    member: { type: Object, required: true, default: () => {} },
    levelList: { type: Array, required: true, default: () => [] },
  },
  setup (props, { emit }) {
    const { useCompanyContactMenu, getShopContactMenuInfo, orgTaxId } = useCompany()
    const { shopId } = useShop()
    const { simpleFetch } = useFetch()
    const { checkAction, checkActionByUserFeatures } = usePermissions()
    const { memberTopInfo } = useMemberContext()
    const orgId = computed(() => get(store.state, 'org.org.id'))
    const useFeatures = computed(() => {
      return {
        viewRestrictedFile: checkAction('admin.userRestrictedFile.getDownloadUrl'),
        viewMemberLevel: checkAction('admin.memberLevelConfig.page'),
        updateMember: checkAction('admin.member.update'),
        refreshMemberLineProfile: checkAction('admin.member.refreshLineProfile'),
        deleteMemberAuthLine: checkAction('admin.member.deleteMemberAuthLine'),
      }
    })

    const opentixBinded = ref(false)
    const showMemberLevelDetail = ref(false)
    const showProgressDrawer = ref(false)
    const memberPolicyData = reactive({
      configData: {},
      isFirstConfig: false,
    })
    const memberId = computed(() => get(props.member, 'id'))
    const useMemberOpentix = computed(() => checkAction('admin.memberOpentix.page'))
    const usePospal = computed(() => checkAction('admin.shopCustomModule.posPalMemberWallet'))
    const useTKHSPC = computed(() => checkAction('planModule.tkhspc.enable'))
    const useCyjPos = computed(() => checkAction('planModule.cyjPos.enable'))

    /**
     * 來源分店（CNCY-10069）：讀取後端回傳的 UserInfo.sourceShopId / sourceShopName。
     * - 有 sourceShopName → 顯示分店名稱
     * - 有 sourceShopId 但無對應 Shop（查無）→ 顯示「未知分店」（SD §13 SR-8 fallback）
     * - 皆無（既有會員未回填）→ 顯示「未記錄」（O3 決策）
     * 不再 fallback 到 member.Shop.name（當前所屬分店），避免與「來源分店」語意混淆。
     */
    const sourceShopName = computed(() => {
      const name = get(props.member, 'sourceShopName')
      if (name) return name
      const sourceShopId = get(props.member, 'sourceShopId')
      if (sourceShopId) return '未知分店'
      return '未記錄'
    })

    const displayData = computed(() => {
      const memberPolicyStatus = get(props.member, 'UserInfo.hasAgreedRightPolicy')
      return {
        memberPolicyStatus: memberPolicyStatus ? t('member.tab.memberInfo.agreed') : t('member.tab.memberInfo.notAgreed'),
      }
    })
    const opentixConfigData = ref([])

    // last member auth line log
    const lastMemberAuthLineLog = computed(() => ({
      // unBindReasean: get(props.member, 'lastMemberAuthLineLog.unBindReasean') || '尚未完成 Line 綁定或已封鎖官方帳號',
      unBindReasean: t('member.tab.memberInfo.lineBindingIncomplete'),
      operation: get(props.member, 'lastMemberAuthLineLog.operation') || '-',
      createdAt: get(props.member, 'lastMemberAuthLineLog.createdAt')
        ? formatDate(get(props.member, 'lastMemberAuthLineLog.createdAt'), 'YYYY/MM/DD HH:mm')
        : null,
    }))

    const checkMemberOpentixBinding = async () => {
      if (!opentixConfigData.value.length) return
      if (!useMemberOpentix.value) return
      await simpleFetch(CheckMemberOpentixBinding, {
        shopId: shopId.value,
        memberId: get(props.member, 'id'),
      }, (res) => {
        opentixBinded.value = res.isBind
      })
    }

    /**
     * 權益政策同意狀態（CBGISSUE-301）：改用 checkActionByUserFeatures 只驗「使用者角色權限」。
     * 權益政策是組織層設定（RightPolicyConfig 掛在 Org 底下），三支 API 都走 org 層路由
     * AuthUserWithOrgAccessControl，後端只驗角色不驗店家方案（方案檢查只發生在 authShop()）。
     * 原本用 checkAction 會連「店家合約方案」一起驗，但 admin.rightPolicyConfig.* 從未被任何
     * 方案來源授予，導致方案側恆為 false、連 orgAdmin 也拿不到 config，欄位永遠不顯示。
     * 仍保留角色權限判斷，以維持 CNCY-3772「店家管理員/店長不跳權限不足錯誤」的修正。
     */
    const getMemberPolicyConfig = async () => {
      if (!checkActionByUserFeatures('admin.rightPolicyConfig.singleFind')) return
      const [res, err] = await GetMemberPolicyConfig({ orgId: orgId.value })
      if (err) {
        window.$message.error(err)
        return
      }
      memberPolicyData.configData = res
      if (!res.id) memberPolicyData.isFirstConfig = true
    }

    const getOpentixConfig = async () => {
      await simpleFetch(GetCustomModule, {
        shopId: shopId.value,
        type: 'opentix',
      }, (res) => {
        opentixConfigData.value = res
      })
    }

    const onViewFile = async (fileId) => {
      const [res, err] = await GetRestrictedFileUrl({
        shopId: shopId.value,
        memberId: memberId.value,
        userRestrictedFileId: fileId,
      })
      if (err) {
        window.$message.error(err)
        return
      }
      console.log(res)
      viewerApi({
        images: [res.url],
        options: {
          toolbar: false,
          navbar: false,
          title: false,
        },
      })
    }

    // LINE 認證相關資料和方法
    const lineAuthData = reactive({
      isMemberAuthLine: false,
      memberAuthLine: null,
      lastMemberAuthLineLog: null,
    })
    const showDeBindDialog = ref(false)
    const refreshLoading = ref(false)

    // 取得會員 LINE 認證資料
    const getMemberAuthLine = async () => {
      try {
        const [res, err] = await GetMemberAuthLine({
          shopId: shopId.value,
          memberId: memberId.value,
        })
        if (err) {
          console.error('取得 LINE 認證資料失敗:', err)
          return
        }

        lineAuthData.isMemberAuthLine = res.isMemberAuthLine
        lineAuthData.memberAuthLine = res.MemberAuthLine
        lineAuthData.lastMemberAuthLineLog = res.lastMemberAuthLineLog
      } catch (error) {
        console.error('取得 LINE 認證資料發生錯誤:', error)
      }
    }

    // 取得會員更完整資訊（使用 FindMember）
    const fetchedMember = ref(null)
    const displayMember = computed(() => (props.member && !isEmptyObj(props.member)) ? props.member : (fetchedMember.value || {}))
    const fetchMemberDetail = async () => {
      try {
        const res = await FindMember({ shopId: shopId.value, id: memberId.value })
        if (!res) return

        const member = res
        // 更新本地 fetchedMember，options API 的 methods 可以優先使用此值
        fetchedMember.value = member
      } catch (err) {
        console.error('fetchMemberDetail error', err)
      }
    }

    // 手動同步 LINE 資料
    const onRefreshLineProfile = async () => {
      if (refreshLoading.value) return
      if (!useFeatures.value.refreshMemberLineProfile) {
        return
      }

      refreshLoading.value = true
      try {
        const [, err] = await RefreshMemberLineProfile({
          shopId: shopId.value,
          memberId: memberId.value,
        })
        if (err) {
          window.$message.error(`${t('member.tab.memberInfo.syncFailed')}${err}`)
          return
        }

        window.$message.success(t('member.tab.memberInfo.lineDataSyncSuccess'))
        // 重新取得資料
        await getMemberAuthLine()
        emit('update')
      } catch (error) {
        console.error('同步 LINE 資料發生錯誤:', error)
        window.$message.error(t('member.tab.memberInfo.syncFailedTryLater'))
      } finally {
        refreshLoading.value = false
      }
    }

    // 解綁成功回調
    const onDeBindSuccess = () => {
      showDeBindDialog.value = false
      // 重新取得資料
      getMemberAuthLine()
      // 觸發父元件更新
      emit('update')
    }

    // 組件掛載時取得 LINE 資料
    onMounted(() => {
      getMemberAuthLine()
      fetchMemberDetail()
    })

    return {
      get,
      fetchMemberDetail,
      fetchedMember,
      displayMember,
      orgId,
      onViewFile,
      useCompanyContactMenu,
      getShopContactMenuInfo,
      orgTaxId,
      showMemberLevelDetail,
      showProgressDrawer,
      memberId,
      opentixBinded,
      checkMemberOpentixBinding,
      useMemberOpentix,
      getOpentixConfig,
      checkAction,
      memberPolicyData,
      getMemberPolicyConfig,
      displayData,
      sourceShopName,
      useFeatures,
      usePospal,
      useTKHSPC,
      useCyjPos,
      lastMemberAuthLineLog,
      // LINE 相關
      lineAuthData,
      showDeBindDialog,
      refreshLoading,
      getMemberAuthLine,
      onRefreshLineProfile,
      onDeBindSuccess,
      memberTopInfo,
    }
  },
  data: () => ({
    showEditDialog: false,
    company: [],
    formData: {
      MemberAuthLine: false,
      isBlock: false,
      memberStatus: 'normal',
      level: '',
      recognizedTimeReset: null,
      recognizedTimeResetValue: null,
      User: {},
      custom: {}, // 自定義欄位資料
      systemCustom: {},
    },
    userInfoSetting: [],
  }),

  computed: {
    ...mapGetters(['shop', 'userPlanFeature', 'userFeatures']),
    baseUserSetting () {
      return this.userInfoSetting.filter((field) => field.isBase)
    },
    customUserSetting () {
      return this.userInfoSetting.filter((field) => !field.isBase)
    },
    getBaseFormData () {
      return (field) => {
        if (field.type === 'gender') {
          return get(genderName, this.formData[field.key], '-')
        }
        if (field.type === 'birthday') {
          const birthday = get(this.formData, field.key)
          if (!birthday) return t('member.tab.memberInfo.notFilled')
          return formatDate(birthday, 'YYYY/MM/DD')
        }
        return this.formData[field.key]
      }
    },
    getCustomFormData () {
      const customFormData = this.formData.custom || {}
      return (field) => {
        if (field.type === 'checkbox') {
          const fieldData = get(customFormData, field.key) || []
          if (Array.isArray(fieldData)) return fieldData.join('、')
          if (typeof fieldData === 'string') return fieldData
        }
        if (field.type === 'radioSubField') {
          const fieldData = get(customFormData, field.key)
          if (fieldData && fieldData.parentValue && fieldData.childValue) {
            return `${fieldData.parentValue} - ${fieldData.childValue}`
          }
          return '-'
        }
        if (field.type === 'checkboxSubField') {
          const fieldData = get(customFormData, field.key) || []
          if (Array.isArray(fieldData) && fieldData.length > 0) {
            // Group by parentValue
            const group = {}
            fieldData.forEach(item => {
              if (item.parentValue && item.childValue) {
                if (!group[item.parentValue]) {
                  group[item.parentValue] = []
                }
                group[item.parentValue].push(item.childValue)
              }
            })
            // Format: parentValue - childValue1、childValue2, ...
            return Object.entries(group)
              .map(([parent, children]) => `${parent} - ${children.join('、')}`)
              .join('\n')
          }
          return '-'
        }
        if (field.type === 'companyDirectoryFirmTaxId') {
          const taxId = customFormData[field.key]
          const company = find(this.company, { taxId })
          if (!company) return '-'
          return `${company.name} (${taxId})`
        }
        return customFormData[field.key]
      }
    },
    showCompany () {
      return this.useCompanyContactMenu && this.orgTaxId(this.customUserSetting)
    },
  },

  watch: {
    member () {
      console.log('member info tab member watch')
      this.syncForm()
    },
    memberTopInfo: {
      handler () {
        console.log('member info tab memberTopInfo watch')
        this.syncForm()
      },
    },
  },

  async mounted () {
    if (this.checkAction('admin.shopCustomModule.find')) {
      await this.getOpentixConfig()
      this.checkMemberOpentixBinding()
    }
    await this.refresh()
  },

  methods: {
    formatDate,

    async getCompany () {
      let start = 0
      const limit = 10
      const data = []
      let temp = []
      const [res, err] = await SimpleFindCompanyContactMenu({
        shopId: this.shop,
        start,
        limit,
      })
      if (err) {
        this.$message.error(err)
        return
      }
      start += res.length
      temp = res
      data.push(...res)

      while (temp.length === limit) {
        const [res, err] = await SimpleFindCompanyContactMenu({
          shopId: this.shop,
          start,
          limit,
        })
        if (err) {
          this.$message.error(err)
          return
        }
        start += res.length
        temp = res
        data.push(...res)
      }
      this.company = data
    },
    memberOrigin (origin, originText) {
      return handleMemberOrigins({ origin, originText })
    },
    async refresh () {
      await Promise.allSettled([
        this.getMemberPolicyConfig(),
        this.getUserInfoSetting(),
        this.getShopContactMenuInfo({ shopId: this.shop }),
        this.getMemberAuthLine(),
        this.fetchMemberDetail(),
        this.$refs.progressBlock?.refresh(),
      ])
      if (this.showCompany) {
        this.getCompany()
      }
      await this.$nextTick()
      this.syncForm()
    },
    getProp (target, key) {
      if (!target) return t('member.tab.memberInfo.noLevel')
      return target[key]
    },

    getDisplayLevel () {
      const curLevel = this.member.level
      if (!curLevel) {
        return this.memberTopInfo?.memberConfig?.nullLevelName || t('member.tab.memberInfo.noLevel')
      }
      return this.levelList?.find(({ level }) => curLevel === level)
        ?.name
    },

    syncForm () {
      // 優先使用 props.member，因為它是從父層傳入的最新資料
      // fetchedMember 僅在 onMounted 時取得一次，可能會過時
      // const source = (this.member && !isEmptyObj(this.member)) ? this.member : (this.fetchedMember || {})
      const source = this.fetchedMember || {}

      const userInfo = source.UserInfo || {}
      const user = source.User || {}

      this.formData.id = source.id
      this.formData.isBlock = source.isBlock
      this.formData.memberStatus = source.memberStatus || (source.isBlock ? 'blocked' : 'normal')
      this.formData.level = get(this.memberTopInfo, 'member.level') || source.level
      this.formData.MemberAuthLine = source.MemberAuthLine
      this.formData.User = user

      this.formData.name = userInfo.name
      this.formData.phone = get(this.memberTopInfo, 'memberUserInfo.phone') || userInfo.phone
      this.formData.birthday = userInfo.birthday
      this.formData.address = userInfo.address
      this.formData.gender = userInfo.gender
      this.formData.email = userInfo.email
      this.formData.levelExpiredAt = source.levelExpiredAt
      this.formData.levelNote = get(this.fetchedMember, 'levelNote') || source.levelNote
      this.formData.forever = !source.levelExpiredAt
      this.formData.isLevelRemainFulfilled = source.isLevelRemainFulfilled
      this.formData.recognizedTimeReset = source.recognizedTimeReset ?? null
      this.formData.recognizedTimeResetValue = source.recognizedTimeResetValue ?? null
      this.formData.systemCustom = userInfo.systemCustom || {}
      this.tagCheckList = extractList('id', source.MTags || [])
      const customList = Object.keys(userInfo.custom || {})
      if (customList.length) {
        customList.forEach((item) => {
          this.formData.custom = {
            ...this.formData.custom,
            [item]: userInfo.custom[item],
          }
        })
      }
    },

    async getUserInfoSetting () {
      const [res, err] = await GetUserInfoSetting({
        orgId: this.orgId,
      })

      if (err) {
        if (err.status === 404) return
        return this.$message.error(err)
      }

      this.userInfoSetting = res.fields
    },
    /**
     * 根據 memberStatus 返回 Tag 類型
     * @param {string} status - 'normal' | 'warning' | 'blocked'
     * @param {boolean} [isBlockFallback] - 舊欄位向後相容
     */
    memberStatusTagType (status, isBlockFallback) {
      const resolved = status || (isBlockFallback ? 'blocked' : 'normal')
      if (resolved === 'blocked') return 'danger'
      if (resolved === 'warning') return 'warning'
      return 'info'
    },
    /**
     * 根據 memberStatus 返回顯示文字
     * @param {string} status - 'normal' | 'warning' | 'blocked'
     * @param {boolean} [isBlockFallback] - 舊欄位向後相容
     */
    memberStatusLabel (status, isBlockFallback) {
      const resolved = status || (isBlockFallback ? 'blocked' : 'normal')
      if (resolved === 'blocked') return t('member.tab.memberInfo.blockedMember')
      if (resolved === 'warning') return t('member.tab.memberInfo.warningMember')
      return t('member.tab.memberInfo.normalMember')
    },
    async onChangeBlock () {
      // 狀態變更已改由 MemberProfileEdit 彈窗處理，此方法保留之向後相容
    },
  },
})
</script>

<style scoped lang="postcss">
::v-deep .el-select .el-input {
  @apply h-full;
}
::v-deep .el-tag--info {
  @apply h-full break-all;
  white-space: break-spaces;
}
::v-deep .el-select__tags {
  @apply flex flex-wrap;
}

.bind-phone-btn {
  @apply cursor-pointer text-left;
  @apply absolute right-[-88px] top-[2px];
}

.icon__vertical-align {
  @apply inline-flex align-middle;
  > img {
    @apply mr-1;
  }
}
</style>
